import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Reveal `.reveal` elements as they scroll into view.
 *
 * Mounted once at the layout level and re-run on navigation.
 *
 * The important detail is that this cannot just observe what exists at mount.
 * The list pages render their `.reveal` wrapper only after a fetch resolves, so
 * on a slow connection the element appears long after this hook has run — it was
 * never observed, the one-shot failsafe had already fired, and the list sat at
 * opacity 0 permanently. A MutationObserver picks up late arrivals, and every
 * element gets its own failsafe timer rather than there being a single global
 * one that can expire before the content exists.
 *
 * Two further deliberate choices:
 *
 * - The hiding rule lives behind `.js-reveal` on <html>, which this hook adds.
 *   If the bundle fails or scripting is off, the class is never set and every
 *   section renders normally. Content must not depend on script to be readable.
 *
 * - Elements are unobserved once shown. These are entrances, not scroll-linked
 *   effects; re-hiding something the reader has already passed is a distraction.
 */
const FAILSAFE_MS = 2500;

export default function useReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    const root = document.documentElement;

    // No IntersectionObserver, or the visitor asked for less motion: leave
    // everything visible rather than degrading to a blank page.
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!("IntersectionObserver" in window) || reduced) {
      root.classList.remove("js-reveal");
      return undefined;
    }

    root.classList.add("js-reveal");

    const timers = new Set();
    const show = (el) => {
      el.classList.add("is-visible");
      observer.unobserve(el);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) show(entry.target);
        }
      },
      // A negative bottom margin starts the entrance a little before the element
      // reaches the fold, so it has finished by the time it is read.
      { rootMargin: "0px 0px -12% 0px", threshold: 0.05 }
    );

    const track = (el) => {
      if (el.classList.contains("is-visible") || el.dataset.revealTracked) return;
      el.dataset.revealTracked = "1";

      // Already on screen when it appeared: show it now rather than waiting for
      // the observer's first callback, which lands after a paint.
      if (el.getBoundingClientRect().top < window.innerHeight) {
        el.classList.add("is-visible");
        return;
      }

      observer.observe(el);
      // Per-element safety net. Content hidden until script says otherwise is
      // content that can be lost — a wedged observer, a zero-height viewport
      // mid-resize, an environment that reports nothing as intersecting. A
      // missed animation is nothing; an invisible page is everything.
      const t = setTimeout(() => {
        timers.delete(t);
        show(el);
      }, FAILSAFE_MS);
      timers.add(t);
    };

    const scan = () => document.querySelectorAll(".reveal:not(.is-visible)").forEach(track);
    scan();

    // Catches everything that mounts after a fetch resolves.
    const mutations = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.classList?.contains("reveal")) track(node);
          node.querySelectorAll?.(".reveal:not(.is-visible)").forEach(track);
        }
      }
    });
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      timers.forEach(clearTimeout);
      mutations.disconnect();
      observer.disconnect();
      // Clear the marker so a re-mount on the next route re-tracks these nodes.
      document.querySelectorAll("[data-reveal-tracked]").forEach((el) => {
        delete el.dataset.revealTracked;
      });
    };
  }, [pathname]);
}
